import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import {
  getInitialLocalRobotState,
  LAYERS_LIST_GATES_KEY,
  LAYERS_LIST_LANDMARKS_KEY,
  LAYERS_LIST_WAYPOINTS_KEY,
  LAYERS_LIST_ZONES_KEY,
} from './local-robot-slice-definitions';
import { setSelectedRobotId } from 'store/robots/robots-slice';
import {
  deleteCleaningZoneById,
  deleteLandmarkById,
  deleteRedZoneById,
  deleteRuleZoneById,
  deleteRobotMissionById,
  postNewRobotMission,
  patchRobotMission,
  getGlobalMissions,
  deleteRobotScheduleById,
  postNewRobotSchedule,
  patchRobotSchedule,
  patchEnableAllSchedules,
  patchDisableAllSchedules,
  deleteGateById,
  getFloorsByRobotId,
  patchCleaningZone,
  patchLandmark,
  patchRedZone,
  patchRuleZone,
  postNewCleaningZone,
  postNewLandmark,
  postNewRedZone,
  postNewRuleZone,
  patchLimitZone,
  patchFloor,
  patchRoomById,
  upsertWaypointSet,
  postNewGate,
  patchGateById,
  getErrors,
} from './local-robot-actions';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import ZoneControllerMouseState from '@common/enums/robot/zone-planner/zone-controller-mouse-state';
import ZonePlannerModeState from '@common/enums/robot/zone-planner/zone-planner-mode-state';
import { StationDrag } from '@common/interfaces/robot/rooms/station';
import Point from '@common/interfaces/robot/rooms/point';
import {
  filterSchedulesByFloor,
  getNewName,
  isPointInsidePolygon,
  selectGateByPointId,
  selectRepeatPathByZoneId,
  selectSelectedRoom,
  selectWaypointNodeById,
  selectZoneById,
  shiftZonePoints,
  updateStateWithRoomApiData,
  updateStateWithRoomWaypointSetData,
} from './state-helpers';
import {
  isCleaningZone,
  isTeachAndRepeatZone,
  newCleaningZone,
} from '@common/interfaces/robot/rooms/cleaning-zone';
import { newTeachAndRepeatZonePath } from '@common/interfaces/robot/rooms/teach-and-repeat-zone-path';
import { isOffsetInPolygon } from '@common/validation/zone-validation';
import ZonePointType, { SimpleZonePointType } from '@common/enums/robot/rooms/zone-point-type';
import MapHistoryEntry, {
  isHistoryCleaningZone,
  isHistoryGateSet,
  isHistoryTeachAndRepeatZonePath,
  MapHistoryAction,
  MapHistoryType,
} from '@common/interfaces/robot/zone-planner/map-history';
import RedZone, { newRedZone } from '@common/interfaces/robot/rooms/red-zone';
import RuleZone, { isRuleZone, newRuleZone } from '@common/interfaces/robot/rooms/rule-zone';
import IdTypePoint from '@common/interfaces/robot/rooms/id-type-point';
import RoomData from '@common/interfaces/robot/rooms/room-data';
import {
  copyNode,
  createEdge,
  isUnsavedWaypointSet,
  WaypointNode,
} from '@common/interfaces/robot/rooms/waypoints';
import { mapFloorApiDataToFloorData } from '@common/interfaces/robot/rooms/floor-data';
import i18n from 'i18n';
import { NavigatorName, AutoFillInNavigator } from '@common/enums/command-hub/navigator';
import Landmark, { copyLandmark, LandmarkType } from '@common/interfaces/robot/rooms/landmark';
import ScheduleEntry, {
  mapScheduleEntryToScheduleEntryState,
  validateSchedulesDontOverlap,
  validateScheduleIsNotTooLong,
  isScheduleInFloor,
} from '@common/interfaces/robot/scheduling/schedule-entry';
import TimeSpan from '@common/utils/time-span';
import Weekday, { getShiftedWeekday } from '@common/utils/weekday';
import SchedulerControllerMouseState from '@common/enums/robot/rooms/scheduler-controller-mouse-state';
import { updateDataList } from 'utils/generic-data-list-handlers';
import RestSet from '@common/interfaces/api/rest-set';
import { getAllowedZoneShift } from '@common/utils/map/zone-helper';
import WebSocketMessage from 'models/misc/api/websocket-message';
import WebSocketMessageType from 'models/enums/websocket-message-type';
import {
  isTeachingUpdateMessage,
  TeachingUpdateType,
} from '@common/interfaces/robot/rooms/hub-messages/teaching-update-message';
import { PathPoint } from '@common/interfaces/robot/rooms/teach-and-repeat-zone-path';
import { copyGate } from '@common/interfaces/robot/rooms/gate';

const initialLocalRobotState = getInitialLocalRobotState();

const localRobotSlice = createSlice({
  name: 'local-robot',
  initialState: initialLocalRobotState,
  reducers: {
    resetLocalRobotState(state) {
      return {
        ...initialLocalRobotState,
      };
    },
    setSelectedRoomId(state, action: PayloadAction<string | null>) {
      state.rooms.selectedId = action.payload;
      state.zonePlanner.history.redo = [];
      state.zonePlanner.history.undo = [];
      state.zonePlanner.selectedZone = {
        id: null,
        prevDragPoint: null,
        selectedPointId: null,
      };
      state.landmarks.selectedId = null;
      state.waypoints.mousePosition = null;
      state.waypoints.selectedEdgeId = null;
      state.waypoints.selectedNodeId = null;
      state.zonePlanner.mouseMode = ZoneControllerMouseState.Default;
    },
    toggleAllZonesHidden(state, action: PayloadAction<{ ids: string[]; showAll: boolean }>) {
      action.payload.ids.forEach((zoneId) => {
        if (state.zonePlanner.hiddenZoneIds.includes(zoneId) && action.payload.showAll) {
          state.zonePlanner.hiddenZoneIds = state.zonePlanner.hiddenZoneIds.filter(
            (id) => id !== zoneId,
          );
        } else if (!state.zonePlanner.hiddenZoneIds.includes(zoneId) && !action.payload.showAll) {
          state.zonePlanner.hiddenZoneIds.push(zoneId);
        }
      });
      localStorage.setItem(LAYERS_LIST_ZONES_KEY, JSON.stringify(state.zonePlanner.hiddenZoneIds));
    },
    toggleAllLandmarksHidden(state, action: PayloadAction<{ ids: string[]; showAll: boolean }>) {
      action.payload.ids.forEach((id) => {
        if (state.zonePlanner.hiddenLandmarkIds.includes(id) && action.payload.showAll) {
          state.zonePlanner.hiddenLandmarkIds = state.zonePlanner.hiddenLandmarkIds.filter(
            (hiddenId) => id !== hiddenId,
          );
        } else if (!state.zonePlanner.hiddenLandmarkIds.includes(id) && !action.payload.showAll) {
          state.zonePlanner.hiddenLandmarkIds.push(id);
        }
      });
      localStorage.setItem(
        LAYERS_LIST_LANDMARKS_KEY,
        JSON.stringify(state.zonePlanner.hiddenLandmarkIds),
      );
    },
    toggleAllGatesHidden(state, action: PayloadAction<{ ids: string[]; showAll: boolean }>) {
      action.payload.ids.forEach((gateId) => {
        if (state.zonePlanner.hiddenGateIds.includes(gateId) && action.payload.showAll) {
          state.zonePlanner.hiddenGateIds = state.zonePlanner.hiddenGateIds.filter(
            (id) => id !== gateId,
          );
        } else if (!state.zonePlanner.hiddenGateIds.includes(gateId) && !action.payload.showAll) {
          state.zonePlanner.hiddenGateIds.push(gateId);
        }
      });
      localStorage.setItem(LAYERS_LIST_GATES_KEY, JSON.stringify(state.zonePlanner.hiddenGateIds));
    },
    toggleZoneIdHidden(state, action: PayloadAction<string>) {
      if (state.zonePlanner.hiddenZoneIds.includes(action.payload)) {
        state.zonePlanner.hiddenZoneIds = state.zonePlanner.hiddenZoneIds.filter(
          (id) => id !== action.payload,
        );
      } else {
        state.zonePlanner.hiddenZoneIds.push(action.payload);
      }
      localStorage.setItem(LAYERS_LIST_ZONES_KEY, JSON.stringify(state.zonePlanner.hiddenZoneIds));
    },
    toggleLandmarkIdHidden(state, action: PayloadAction<string>) {
      if (state.zonePlanner.hiddenLandmarkIds.includes(action.payload)) {
        state.zonePlanner.hiddenLandmarkIds = state.zonePlanner.hiddenLandmarkIds.filter(
          (id) => id !== action.payload,
        );
      } else {
        state.zonePlanner.hiddenLandmarkIds.push(action.payload);
      }
      localStorage.setItem(
        LAYERS_LIST_LANDMARKS_KEY,
        JSON.stringify(state.zonePlanner.hiddenLandmarkIds),
      );
    },
    toggleGateIdHidden(state, action: PayloadAction<string>) {
      if (state.zonePlanner.hiddenGateIds.includes(action.payload)) {
        state.zonePlanner.hiddenGateIds = state.zonePlanner.hiddenGateIds.filter(
          (id) => id !== action.payload,
        );
      } else {
        state.zonePlanner.hiddenGateIds.push(action.payload);
      }
      localStorage.setItem(LAYERS_LIST_GATES_KEY, JSON.stringify(state.zonePlanner.hiddenGateIds));
    },
    toggleWayPointVisibility(state) {
      state.waypoints.isVisible = !state.waypoints.isVisible;
      localStorage.setItem(LAYERS_LIST_WAYPOINTS_KEY, JSON.stringify(state.waypoints.isVisible));
    },
    setZonePlannerMouseState(state, action: PayloadAction<ZoneControllerMouseState>) {
      state.zonePlanner.mouseMode = action.payload;
    },
    setZonePlannerMode(state, action: PayloadAction<ZonePlannerModeState>) {
      state.zonePlanner.plannerMode = action.payload;
    },
    setSelectedLandmark(state, action: PayloadAction<string | null>) {
      state.landmarks.selectedId = action.payload;

      const existingLandmark = state.landmarks.data.find((l) => l.id === action.payload);
      if (
        existingLandmark != null &&
        state.zonePlanner.mouseMode === ZoneControllerMouseState.DragZone
      ) {
        state.zonePlanner.history.redo = [];
        state.zonePlanner.history.undo.push({
          action: MapHistoryAction.Patched,
          type: MapHistoryType.Landmark,
          changedData: { id: action.payload, landmarkPoint: { ...existingLandmark.landmarkPoint } },
        } as MapHistoryEntry);
      }
    },
    setSelectedZone(
      state,
      action: PayloadAction<{ id: string; startDraggingHistory?: boolean } | null>,
    ) {
      state.zonePlanner.selectedZone = {
        id: action.payload?.id ?? null,
        selectedPointId: null,
        prevDragPoint: null,
      };

      state.zonePlanner.currentTeachingAutoZonePath = {
        zoneId: action.payload?.id ?? null,
        selectedTeachAndRepeatPathPointId: null,
      };

      if (action.payload?.startDraggingHistory) {
        const draggedZone = selectZoneById(state, action.payload.id);
        const draggedTRPath = selectRepeatPathByZoneId(state, action.payload.id);
        if (draggedZone != null) {
          const isRuleZoneInstance = isRuleZone(draggedZone);
          const isRedZone = !isRuleZoneInstance && !isCleaningZone(draggedZone);
          state.zonePlanner.history.redo = [];
          state.zonePlanner.history.undo.push({
            action: MapHistoryAction.Patched,
            type: isRuleZoneInstance
              ? MapHistoryType.RuleZone
              : isRedZone
                ? MapHistoryType.RedZone
                : MapHistoryType.CleaningZone,
            changedData: {
              id: draggedZone.id,
              points: [
                ...draggedZone.points.map((p) => ({
                  orderIndex: p.orderIndex,
                  point: { ...p.point },
                })),
              ],
              startPosition:
                isRedZone || isRuleZoneInstance ? undefined : { ...draggedZone.startPosition },
            },
            additionalChangedData: draggedTRPath,
          } as MapHistoryEntry);
        }
      }
    },
    sendZoneToBack(state, action: PayloadAction<string>) {
      let index = state.zonePlanner.zoneIdsSentToBackOrder.findIndex((id) => id === action.payload);
      if (index !== -1) {
        state.zonePlanner.zoneIdsSentToBackOrder.splice(index, 1);
      }
      state.zonePlanner.zoneIdsSentToBackOrder.unshift(action.payload);
    },
    patchStationDrag(state, action: PayloadAction<Partial<StationDrag>>) {
      state.zonePlanner.chargingStationDrag = {
        ...state.zonePlanner.chargingStationDrag,
        ...action.payload,
      };
    },
    updateSelectedZonePointCoordinates(
      state,
      action: PayloadAction<{
        pointId: string;
        point: Point;
        forcePosition?: boolean;
        saveHistory?: boolean;
      }>,
    ) {
      const selectedZoneInstance =
        state.zonePlanner.selectedZone.id == null
          ? null
          : selectZoneById(state, state.zonePlanner.selectedZone.id);
      if (selectedZoneInstance != null) {
        let point = selectedZoneInstance.points.find(
          (p) => p.point.id === action.payload.pointId,
        )?.point;
        if (
          !point &&
          isCleaningZone(selectedZoneInstance) &&
          selectedZoneInstance.startPosition?.id === action.payload.pointId
        ) {
          if (
            action.payload.forcePosition ||
            (isPointInsidePolygon(
              selectedZoneInstance.points.map((p) => p.point),
              action.payload.point,
            ) &&
              isOffsetInPolygon(
                selectedZoneInstance.points.map((p) => p.point),
                action.payload.point,
              ))
          ) {
            point = selectedZoneInstance.startPosition;
          }
        }

        if (point) {
          point.coordinateX = action.payload.point.coordinateX;
          point.coordinateY = action.payload.point.coordinateY;
        }
      }
    },
    addSelectedZonePoint(
      state,
      action: PayloadAction<{
        point: Point | null;
        type: SimpleZonePointType;
        id: string | null;
        insertAtIndex: number | null;
        unselectPoint?: boolean;
        individualPointCreated?: boolean;
      }>,
    ) {
      if (action.payload.point == null || state.rooms.selectedId == null) {
        return;
      }

      if (state.zonePlanner.selectedZone.id == null) {
        if (state.zonePlanner.mouseMode === ZoneControllerMouseState.AddCleaningZone) {
          const newZoneName = getNewName(
            i18n.t('cleaning-zone'),
            state.cleaningZones.data
              .filter((c) => c.name != null && c.room === state.rooms.selectedId)
              .map((c) => c.name!),
          );
          const newZone = newCleaningZone(newZoneName, AutoFillInNavigator, state.rooms.selectedId);
          state.cleaningZones.data.push(newZone);
          state.zonePlanner.selectedZone.id = newZone.id;
        } else if (
          [ZoneControllerMouseState.AddRedZone, ZoneControllerMouseState.AddRedZoneLine].includes(
            state.zonePlanner.mouseMode,
          )
        ) {
          const isEvacuationZone =
            state.zonePlanner.plannerMode === ZonePlannerModeState.EvacuationMode;
          const newZoneName = getNewName(
            isEvacuationZone ? i18n.t('evacuation-red-zone') : i18n.t('red-zone'),
            state.redZones.data
              .filter((r) => r.name != null && r.room === state.rooms.selectedId)
              .map((r) => r.name!),
          );
          const newZone = newRedZone(newZoneName, state.rooms.selectedId, isEvacuationZone);
          state.redZones.data.push(newZone);
          state.zonePlanner.selectedZone.id = newZone.id;
        } else if (state.zonePlanner.mouseMode === ZoneControllerMouseState.AddRuleZone) {
          const newZoneName = getNewName(
            i18n.t('rule-zone'),
            state.ruleZones.data
              .filter((r) => r.name != null && r.room === state.rooms.selectedId)
              .map((r) => r.name!),
          );
          const newZone = newRuleZone(newZoneName, state.rooms.selectedId);
          state.ruleZones.data.push(newZone);
          state.zonePlanner.selectedZone.id = newZone.id;
        } else if (state.zonePlanner.mouseMode === ZoneControllerMouseState.AddTeachAndRepeatZone) {
          const newZoneName = getNewName(
            i18n.t('teachandrepeat-zone'),
            state.cleaningZones.data
              .filter((c) => c.name != null && c.room === state.rooms.selectedId)
              .map((c) => c.name!),
          );
          const newZone = newCleaningZone(
            newZoneName,
            NavigatorName.RepeatPath,
            state.rooms.selectedId,
          );
          state.cleaningZones.data.push(newZone);
          state.zonePlanner.selectedZone.id = newZone.id;

          const newTrPath = newTeachAndRepeatZonePath(newZone.id, newZone.room);
          state.teachRepeatPaths.data.push(newTrPath);
        }
      }

      let selectedZoneInstance =
        state.zonePlanner.selectedZone.id == null
          ? null
          : selectZoneById(state, state.zonePlanner.selectedZone.id);
      if (selectedZoneInstance == null) return;

      if (action.payload.type === SimpleZonePointType.CornerPoint) {
        if (action.payload.individualPointCreated) {
          const isRuleZoneInstance = isRuleZone(selectedZoneInstance);
          const isRedZone = !isRuleZoneInstance && !isCleaningZone(selectedZoneInstance);
          state.zonePlanner.history.redo = [];
          state.zonePlanner.history.undo.push({
            action: MapHistoryAction.Patched,
            type: isRuleZoneInstance
              ? MapHistoryType.RuleZone
              : isRedZone
                ? MapHistoryType.RedZone
                : MapHistoryType.CleaningZone,
            changedData: {
              id: selectedZoneInstance.id,
              points: [
                ...selectedZoneInstance.points.map((p) => ({
                  orderIndex: p.orderIndex,
                  point: { ...p.point },
                })),
              ],
            },
          });
        }

        const newPoint: IdTypePoint = {
          ...action.payload.point,
          id: action.payload.id !== null ? action.payload.id : uuidv4(),
          type: isTeachAndRepeatZone(selectedZoneInstance)
            ? ZonePointType.TeachAndRepeatZoneCorner
            : isCleaningZone(selectedZoneInstance)
              ? ZonePointType.CleaningZoneCorner
              : isRuleZone(selectedZoneInstance)
                ? ZonePointType.RuleZoneCorner
                : ZonePointType.RedZoneCorner,
        };

        let orderIndex = selectedZoneInstance.points.length;
        if (action.payload.insertAtIndex != null) {
          orderIndex = action.payload.insertAtIndex;
          for (let i = orderIndex; i < selectedZoneInstance.points.length; i++) {
            selectedZoneInstance.points[i].orderIndex =
              selectedZoneInstance.points[i].orderIndex + 1;
          }
        }

        selectedZoneInstance.points.push({ orderIndex: orderIndex, point: newPoint });
        selectedZoneInstance.points.sort((p1, p2) => p1.orderIndex - p2.orderIndex);

        state.zonePlanner.selectedZone.selectedPointId =
          action.payload.unselectPoint === true ? null : newPoint.id;
      } else if (
        action.payload.type === SimpleZonePointType.CleaningZoneStartPosition &&
        isCleaningZone(selectedZoneInstance)
      ) {
        selectedZoneInstance.startPosition = {
          ...action.payload.point,
          id: uuidv4(),
          type: ZonePointType.CleaningZoneStartPosition,
        };
      }
    },
    updateHistory(state, action: PayloadAction<MapHistoryEntry>) {
      state.zonePlanner.history.redo = [];
      state.zonePlanner.history.undo.push(action.payload);
    },
    setSelectedGate(state, action: PayloadAction<string | null>) {
      state.gates.selectedId = action.payload;
      state.zonePlanner.selectedZone = {
        id: null,
        prevDragPoint: null,
        selectedPointId: null,
      };
    },
    setSelectedGatePoint(
      state,
      action: PayloadAction<{ pointId: string | null; updateHistory?: boolean }>,
    ) {
      const gate = selectGateByPointId(state, action.payload.pointId);

      if (action.payload.updateHistory === true && gate != null) {
        const changingNode = state.waypoints.data.nodes.find((n) => n.id === gate.mWaypoint);

        state.zonePlanner.history.redo = [];
        state.zonePlanner.history.undo.push({
          action: MapHistoryAction.Patched,
          type: MapHistoryType.Gate,
          changedData: {
            gate: copyGate(gate),
            waypointSet:
              changingNode != null
                ? {
                    edges: [],
                    nodes: [copyNode(changingNode)], // only the middle node will change on a patch of the gate post,
                  }
                : undefined,
          },
        });
      }
      if (action.payload.pointId == null) {
        state.gates.selectedPointId = null;
        state.gates.selectedId = null;
        return;
      }
      if (gate == null) {
        return;
      }
      state.gates.selectedId = gate.id;
      state.gates.selectedPointId = action.payload.pointId;
    },
    patchSelectedGate(
      state,
      action: PayloadAction<{
        id?: string;
        name?: string;
        identifier?: string;
        postCoordinates?: Point;
      }>,
    ) {
      const selectedGate = state.gates.data.find((g) => g.id === state.gates.selectedId);
      if (selectedGate == null) {
        return;
      }
      selectedGate.name = action.payload.name ?? selectedGate.name;
      selectedGate.identifier = action.payload.identifier ?? selectedGate.identifier;

      if (state.gates.selectedPointId != null && action.payload.postCoordinates != null) {
        if (state.gates.selectedPointId === selectedGate.aPost.id) {
          selectedGate.aPost = { ...selectedGate.aPost, ...action.payload.postCoordinates };
        } else if (state.gates.selectedPointId === selectedGate.bPost.id) {
          selectedGate.bPost = { ...selectedGate.bPost, ...action.payload.postCoordinates };
        } else {
          return;
        }
        const middleWayPoint = state.waypoints.data.nodes.find(
          (n) => n.id === selectedGate.mWaypoint,
        );
        if (middleWayPoint != null) {
          middleWayPoint.point.coordinateX =
            (selectedGate.aPost.coordinateX + selectedGate.bPost.coordinateX) / 2;
          middleWayPoint.point.coordinateY =
            (selectedGate.aPost.coordinateY + selectedGate.bPost.coordinateY) / 2;
        }
      }
    },
    updateSelectedZoneCoordinatesOnDrag(
      state,
      action: PayloadAction<{ initialDragPoint: Point; newDragPoint: Point; roomData: RoomData }>,
    ) {
      const selectedZoneInstance =
        state.zonePlanner.selectedZone.id == null
          ? null
          : selectZoneById(state, state.zonePlanner.selectedZone.id);
      if (selectedZoneInstance == null) {
        return;
      }

      const allowedShift = shiftZonePoints(
        selectedZoneInstance,
        action.payload.initialDragPoint,
        action.payload.newDragPoint,
        action.payload.roomData,
      );

      if (isCleaningZone(selectedZoneInstance) && selectedZoneInstance.startPosition != null) {
        selectedZoneInstance.startPosition.coordinateX =
          allowedShift.coordinateX + selectedZoneInstance.startPosition.coordinateX;
        selectedZoneInstance.startPosition.coordinateY =
          allowedShift.coordinateY + selectedZoneInstance.startPosition.coordinateY;
      }
      state.zonePlanner.selectedZone.prevDragPoint = action.payload.newDragPoint;

      if (isTeachAndRepeatZone(selectedZoneInstance)) {
        // if it's a tr zone then there must be a corresponding path
        const trPathInstance =
          state.zonePlanner.selectedZone.id == null
            ? null
            : selectRepeatPathByZoneId(state, state.zonePlanner.selectedZone.id);
        if (trPathInstance == null) {
          console.error(
            'update selected zone coordinates on drag Zone was a teach and repeat zone, but there was no corresponding teach and repeat Path',
          );
          return;
        }
        // we do sanity check that the ids match but technically they should
        trPathInstance.path.forEach((point) => {
          point.coordinateX = allowedShift.coordinateX + point.coordinateX;
          point.coordinateY = allowedShift.coordinateY + point.coordinateY;
        });
      }
    },
    setSelectedZoneSelectedPoint(
      state,
      action: PayloadAction<{ pointId: string | null; isMoving?: boolean }>,
    ) {
      const selectedZoneInstance =
        state.zonePlanner.selectedZone.id == null
          ? null
          : selectZoneById(state, state.zonePlanner.selectedZone.id);
      if (selectedZoneInstance == null) {
        return;
      }

      state.zonePlanner.selectedZone.selectedPointId = action.payload.pointId;
      state.zonePlanner.currentTeachingAutoZonePath.selectedTeachAndRepeatPathPointId = null;

      if (action.payload.isMoving) {
        const isRuleZoneInstance = isRuleZone(selectedZoneInstance);
        const isRedZone = !isRuleZoneInstance && !isCleaningZone(selectedZoneInstance);

        state.zonePlanner.history.redo = [];
        state.zonePlanner.history.undo.push({
          action: MapHistoryAction.Patched,
          type: isRuleZoneInstance
            ? MapHistoryType.RuleZone
            : isRedZone
              ? MapHistoryType.RedZone
              : MapHistoryType.CleaningZone,
          changedData: {
            id: selectedZoneInstance.id,
            points: [
              ...selectedZoneInstance.points.map((p) => ({
                orderIndex: p.orderIndex,
                point: { ...p.point },
              })),
            ],
            startPosition:
              isRedZone || isRuleZoneInstance
                ? undefined
                : { ...selectedZoneInstance.startPosition },
          },
        } as MapHistoryEntry);
      }
    },
    setSelectedWayPoint(
      state,
      action: PayloadAction<{ pointId: string | null; isMoving?: boolean } | null>,
    ) {
      if (action.payload === null) {
        state.waypoints.selectedNodeId = null;
        state.waypoints.selectedEdgeId = null;
        state.waypoints.mousePosition = null;
      } else if (state.waypoints.selectedNodeId === null) {
        state.waypoints.selectedNodeId = action.payload.pointId;
        const room = selectSelectedRoom(state);
        const movedNode = selectWaypointNodeById(state, action.payload.pointId);
        if (movedNode != null && action.payload.isMoving && room != null) {
          state.zonePlanner.history.redo = [];
          state.zonePlanner.history.undo.push({
            action: MapHistoryAction.Patched,
            type: MapHistoryType.WaypointSet,
            changedData: {
              nodes: [...state.waypoints.data.nodes.filter((n) => n.room === room.id)],
              edges: [...state.waypoints.data.edges.filter((n) => n.room === room.id)],
            },
          } as MapHistoryEntry);
        }
      } else {
        const room = selectSelectedRoom(state);
        const initialNode = selectWaypointNodeById(state, state.waypoints.selectedNodeId);
        const endNode = selectWaypointNodeById(state, action.payload.pointId);

        if (initialNode != null && endNode != null && initialNode !== endNode && room != null) {
          const newEdge = createEdge(state.waypoints.data.edges, initialNode, endNode, room.id);
          if (newEdge !== null) {
            state.zonePlanner.history.undo.push({
              action: MapHistoryAction.Patched,
              type: MapHistoryType.WaypointSet,
              changedData: {
                nodes: [...state.waypoints.data.nodes.filter((n) => n.room === room.id)],
                edges: [...state.waypoints.data.edges.filter((n) => n.room === room.id)],
              },
            } as MapHistoryEntry);
            state.waypoints.data.edges.push(newEdge);
            state.zonePlanner.history.redo = [];
            state.waypoints.selectedEdgeId = newEdge.id;
          }
        }
        if (action.payload.pointId !== state.waypoints.selectedNodeId) {
          state.waypoints.mousePosition = null;
          state.waypoints.selectedNodeId = null;
        }
      }
    },
    setRoomLimitZoneSelectedPointId(state, action: PayloadAction<string | null>) {
      const roomLimitZone = state.limitZones.data.find((lz) => lz.room === state.rooms.selectedId);

      if (
        action.payload == null ||
        (roomLimitZone != null && roomLimitZone.points.some((p) => p.point.id === action.payload))
      ) {
        state.limitZones.selectedPointId = action.payload;
      }
    },
    addRoomLimitZonePoint(
      state,
      action: PayloadAction<{
        point: Point;
        insertAtIndex: number | null;
      }>,
    ) {
      const roomLimitZone = state.limitZones.data.find((lz) => lz.room === state.rooms.selectedId);

      if (roomLimitZone == null) {
        return;
      }

      const newPoint: IdTypePoint = {
        ...action.payload.point,
        id: uuidv4(),
        type: ZonePointType.RoomLimitZonePoint,
      };

      let orderIndex = roomLimitZone.points.length;
      if (action.payload.insertAtIndex != null) {
        orderIndex = action.payload.insertAtIndex;
        for (let i = orderIndex; i < roomLimitZone.points.length; i++) {
          roomLimitZone.points[i].orderIndex = roomLimitZone.points[i].orderIndex + 1;
        }
      }

      roomLimitZone.points.push({ orderIndex: orderIndex, point: newPoint });
      roomLimitZone.points.sort((p1, p2) => p1.orderIndex - p2.orderIndex);

      state.limitZones.selectedPointId = newPoint.id;
    },
    addTeachAndRepeatPathPoint(
      state,
      action: PayloadAction<{ point: Point; insertAtIndex: number }>,
    ) {
      let selectedZoneInstance =
        state.zonePlanner.selectedZone.id == null
          ? null
          : selectZoneById(state, state.zonePlanner.selectedZone.id);
      let trPathInstance =
        state.zonePlanner.selectedZone.id == null
          ? null
          : selectRepeatPathByZoneId(state, state.zonePlanner.selectedZone.id);
      if (
        selectedZoneInstance == null ||
        !isTeachAndRepeatZone(selectedZoneInstance) ||
        !trPathInstance ||
        trPathInstance.path == null
      ) {
        return;
      }

      const newPoint: PathPoint = {
        ...action.payload.point,
        coordinateTheta: 0,
        closeToObstacle: -1, // -1 signifies that it is unknown. The webapp doesn't know if a point is close to obstacle
        id: uuidv4(),
        type: ZonePointType.TeachAndRepeatZonePathPoint,
      };

      state.zonePlanner.history.redo = [];
      state.zonePlanner.history.undo.push({
        action: MapHistoryAction.Patched,
        type: MapHistoryType.TeachAndRepeatZonePath,
        changedData: {
          id: trPathInstance.id,
          zoneId: trPathInstance.zoneId,
          path: [...trPathInstance.path],
        },
      });
      
      trPathInstance.path.splice(action.payload.insertAtIndex, 0, newPoint);
      state.zonePlanner.currentTeachingAutoZonePath.selectedTeachAndRepeatPathPointId = newPoint.id;
    },
    updateSelectedTeachAndRepeatPathPointCoordinates(
      state,
      action: PayloadAction<{ pointId: string; point: Point }>,
    ) {
      const selectedZoneInstance =
        state.zonePlanner.selectedZone.id == null
          ? null
          : selectZoneById(state, state.zonePlanner.selectedZone.id);
      const trPathInstance =
        state.zonePlanner.selectedZone.id == null
          ? null
          : selectRepeatPathByZoneId(state, state.zonePlanner.selectedZone.id);
      if (
        selectedZoneInstance == null ||
        !isCleaningZone(selectedZoneInstance) ||
        trPathInstance == null ||
        trPathInstance.path == null
      ) {
        return;
      }

      const pathPointIndex = trPathInstance.path.findIndex((p) => p.id === action.payload.pointId);
      if (pathPointIndex === -1) {
        return;
      }

      trPathInstance.path[pathPointIndex].coordinateX = action.payload.point.coordinateX;
      trPathInstance.path[pathPointIndex].coordinateY = action.payload.point.coordinateY;

      if (pathPointIndex === 0 && selectedZoneInstance.startPosition != null) {
        // 1st point in path is the zone start point -> if it's moved, move zone start point as well
        // nice one! also we didn't move the start point to the path, we just need to make sure that the first point inside the path object is the same as the start field ;)
        selectedZoneInstance.startPosition.coordinateX = action.payload.point.coordinateX;
        selectedZoneInstance.startPosition.coordinateY = action.payload.point.coordinateY;
      }
    },
    setSelectedTeachAndRepeatZonePathPoint(state, action: PayloadAction<string | null>) {
      const selectedZoneInstance =
        state.zonePlanner.selectedZone.id == null
          ? null
          : selectZoneById(state, state.zonePlanner.selectedZone.id);
      const trPathInstance =
        state.zonePlanner.selectedZone.id == null
          ? null
          : selectRepeatPathByZoneId(state, state.zonePlanner.selectedZone.id);
      if (selectedZoneInstance == null || trPathInstance == null) {
        return;
      }

      state.zonePlanner.currentTeachingAutoZonePath.selectedTeachAndRepeatPathPointId =
        action.payload;
      state.zonePlanner.selectedZone.selectedPointId = null;

      state.zonePlanner.history.redo = [];
      state.zonePlanner.history.undo.push({
        action: MapHistoryAction.Patched,
        type: MapHistoryType.TeachAndRepeatZonePath,
        changedData: {
          id: trPathInstance.id,
          zoneId: trPathInstance.zoneId,
          path: [...trPathInstance.path],
        },
      });
    },
    updateRoomLimitZonePointCoordinates(
      state,
      action: PayloadAction<{ pointId: string | null; newPoint: Point }>,
    ) {
      const roomLimitZone = state.limitZones.data.find((lz) => lz.room === state.rooms.selectedId);

      if (roomLimitZone != null && action.payload.pointId != null) {
        let point = roomLimitZone.points.find((p) => p.point.id === action.payload.pointId)?.point;
        if (point) {
          point.coordinateX = action.payload.newPoint.coordinateX;
          point.coordinateY = action.payload.newPoint.coordinateY;
        }
      }
    },
    updateRoomLimitZoneCoordinatesOnDrag(
      state,
      action: PayloadAction<{ initialDragPoint: Point; newDragPoint: Point; roomData: RoomData }>,
    ) {
      const roomLimitZone = state.limitZones.data.find((lz) => lz.room === state.rooms.selectedId);

      if (roomLimitZone == null) {
        return;
      }

      shiftZonePoints(
        roomLimitZone,
        action.payload.initialDragPoint,
        action.payload.newDragPoint,
        action.payload.roomData,
      );

      state.limitZones.prevDragPoint = action.payload.newDragPoint;
    },
    addLandmark(state, action: PayloadAction<{ point: Point; type: LandmarkType }>) {
      const newId = uuidv4();
      const landmark = {
        name: getNewName(
          i18n.t('landmark-point-name-' + action.payload.type),
          state.landmarks.data
            .filter((lm) => lm.type === action.payload.type && lm.room === state.rooms.selectedId)
            .map((lm) => lm.name),
        ),
        id: newId,
        landmarkPoint: action.payload.point,
        type: action.payload.type,
        room: state.rooms.selectedId,
      } as Landmark;
      state.landmarks.data.push(landmark);
      state.landmarks.selectedId = newId;

      state.zonePlanner.history.redo = [];
      state.zonePlanner.history.undo.push({
        action: MapHistoryAction.Created,
        type: MapHistoryType.Landmark,
        changedData: copyLandmark(landmark),
      } as MapHistoryEntry);
    },
    addWayPoint(state, action: PayloadAction<WaypointNode>) {
      const snapshotNodes = [
        ...state.waypoints.data.nodes.filter((n) => n.room === action.payload.room),
      ];
      const snapshotEdges = [
        ...state.waypoints.data.edges.filter((e) => e.room === action.payload.room),
      ];

      if (state.waypoints.selectedNodeId != null) {
        const initialNode = state.waypoints.data.nodes.find(
          (n) => n.id === state.waypoints.selectedNodeId,
        );
        if (initialNode != null) {
          const newEdge = createEdge(
            state.waypoints.data.edges,
            initialNode,
            action.payload,
            action.payload.room,
          );
          if (newEdge == null) {
            state.waypoints.rest.postError = [
              {
                isValid: false,
                error: i18n.t('validation-error-waypoint-repeated-edges-between-nodes'),
              },
            ];
            return;
          }

          state.waypoints.data.nodes.push(action.payload);
          state.waypoints.data.edges.push(newEdge);
          state.waypoints.selectedEdgeId = newEdge.id;
        } else {
          return;
        }
      } else {
        state.waypoints.data.nodes.push(action.payload);
      }

      state.zonePlanner.history.redo = [];
      state.zonePlanner.history.undo.push({
        action: MapHistoryAction.Patched,
        type: MapHistoryType.WaypointSet,
        changedData: {
          nodes: snapshotNodes,
          edges: snapshotEdges,
        },
      } as MapHistoryEntry);

      state.waypoints.mousePosition = null;
      state.waypoints.selectedNodeId = action.payload.id;
    },
    popUndoRedoAction(
      state,
      action: PayloadAction<{ isUndoAction: boolean; updatedAction: MapHistoryEntry }>,
    ) {
      if (action.payload.isUndoAction) {
        state.zonePlanner.history.undo.pop();
        state.zonePlanner.history.redo.push(action.payload.updatedAction);
      } else {
        state.zonePlanner.history.redo.pop();
        state.zonePlanner.history.undo.push(action.payload.updatedAction);
      }
    },
    addUndoableAction(state, action: PayloadAction<MapHistoryEntry>) {
      state.zonePlanner.history.redo = [];
      state.zonePlanner.history.undo.push(action.payload);
    },
    updateWayPointMousePosition(state, action: PayloadAction<Point>) {
      state.waypoints.mousePosition = action.payload;
    },
    setSelectedScheduleEntry(
      state,
      action: PayloadAction<{
        entry: ScheduleEntry | null;
        time: TimeSpan | null;
        day: Weekday | null;
        mouseState: SchedulerControllerMouseState | null;
      } | null>,
    ) {
      if (action.payload === null) {
        if (state.schedules.selectedScheduleEntry.selectedEntry != null) {
          const scheduleEntryIndex = state.schedules.data?.findIndex(
            (s) => s.scheduleEntry.id === state.schedules.selectedScheduleEntry.selectedEntry?.id,
          );
          if (scheduleEntryIndex !== -1) {
            const scheduleEntry = state.schedules.data[scheduleEntryIndex];
            const updatedScheduleEntry = {
              ...scheduleEntry.scheduleEntry,
            };

            state.schedules.data.splice(scheduleEntryIndex, 1);
            state.schedules.data.push({
              scheduleEntry: updatedScheduleEntry,
              isTemporary: false,
              hasBeenEdited: true,
              wasDragged: false,
              isOverlapping:
                !validateSchedulesDontOverlap(
                  state.schedules.data
                    .filter((s) =>
                      filterSchedulesByFloor(
                        s.scheduleEntry,
                        updatedScheduleEntry,
                        state.missions.data,
                        state.rooms.data,
                      ),
                    )
                    .map((s) => s.scheduleEntry),
                  updatedScheduleEntry,
                ).isValid || !validateScheduleIsNotTooLong(updatedScheduleEntry).isValid,
            });
          }
        }
        state.schedules.selectedScheduleEntry = {
          selectedEntry: null,
          initialDay: null,
          initialTime: null,
        };
        state.schedules.schedulerMouseState = SchedulerControllerMouseState.Default;
      } else {
        state.schedules.selectedScheduleEntry = {
          selectedEntry:
            action.payload.entry != null
              ? {
                  ...action.payload.entry,
                  missionId:
                    action.payload.mouseState === SchedulerControllerMouseState.DragSchedule
                      ? ''
                      : action.payload.entry.missionId,
                }
              : null,
          initialDay: action.payload.day,
          initialTime: action.payload.time,
        };
        state.schedules.schedulerMouseState =
          action.payload.mouseState ?? SchedulerControllerMouseState.Default;
      }
    },
    addNewDraftScheduleEntry(
      state,
      action: PayloadAction<{ scheduleEntries: ScheduleEntry[]; isTemporary: boolean }>,
    ) {
      let sortedEntries = [...state.schedules.data];
      action.payload.scheduleEntries.forEach((entry) => {
        sortedEntries.push({
          scheduleEntry: entry,
          hasBeenEdited: true,
          isTemporary: action.payload.isTemporary,
          wasDragged: false,
          isOverlapping:
            !validateSchedulesDontOverlap(
              state.schedules.data
                .filter((s) =>
                  filterSchedulesByFloor(
                    s.scheduleEntry,
                    entry,
                    state.missions.data,
                    state.rooms.data,
                  ),
                )
                .map((s) => s.scheduleEntry),
              entry,
            ).isValid || !validateScheduleIsNotTooLong(entry).isValid,
        });
        state.schedules.data = sortedEntries;
      });
    },
    deleteDraftScheduleEntry(state, action: PayloadAction<string>) {
      const scheduleEntryIndex = state.schedules.data?.findIndex(
        (s) => s.scheduleEntry.id === action.payload,
      );
      if (scheduleEntryIndex !== -1) {
        state.schedules.data.splice(scheduleEntryIndex, 1);
      }
    },
    updateDraftScheduleEntry(state, action: PayloadAction<{ schedule: Partial<ScheduleEntry> }>) {
      const scheduleEntryIndex = state.schedules.data?.findIndex(
        (s) => s.scheduleEntry.id === action.payload.schedule.id,
      );
      if (scheduleEntryIndex !== -1) {
        const scheduleEntry = state.schedules.data[scheduleEntryIndex];

        scheduleEntry.scheduleEntry = {
          ...scheduleEntry.scheduleEntry,
          ...action.payload.schedule,
        };
        scheduleEntry.wasDragged = true;
        scheduleEntry.isOverlapping =
          !validateSchedulesDontOverlap(
            state.schedules.data
              .filter((s) =>
                filterSchedulesByFloor(
                  s.scheduleEntry,
                  scheduleEntry.scheduleEntry,
                  state.missions.data,
                  state.rooms.data,
                ),
              )
              .map((s) => s.scheduleEntry),
            scheduleEntry.scheduleEntry,
          ).isValid || !validateScheduleIsNotTooLong(scheduleEntry.scheduleEntry).isValid;
        if (
          scheduleEntry.scheduleEntry.timeFrom.hours === 23 &&
          scheduleEntry.scheduleEntry.timeFrom.minutes === 59
        ) {
          scheduleEntry.scheduleEntry.weekdayFrom = getShiftedWeekday(
            scheduleEntry.scheduleEntry.weekdayFrom,
            1,
          );
          scheduleEntry.scheduleEntry.timeFrom = { hours: 0, minutes: 0 };
        }
        if (
          scheduleEntry.scheduleEntry.timeTo.hours === 0 &&
          scheduleEntry.scheduleEntry.timeTo.minutes === 0
        ) {
          scheduleEntry.scheduleEntry.weekdayTo = getShiftedWeekday(
            scheduleEntry.scheduleEntry.weekdayTo,
            -1,
          );
          scheduleEntry.scheduleEntry.timeTo = { hours: 23, minutes: 59 };
        }

        if (scheduleEntry.isTemporary) {
          scheduleEntry.isTemporary = false;
        }
      }

      state.schedules.data = state.schedules.data.map((s) => ({
        ...s,
        isOverlapping:
          !validateSchedulesDontOverlap(
            state.schedules.data
              .filter(
                (other) =>
                  other.scheduleEntry.id !== s.scheduleEntry.id &&
                  filterSchedulesByFloor(
                    other.scheduleEntry,
                    s.scheduleEntry,
                    state.missions.data,
                    state.rooms.data,
                  ),
              )
              .map((other) => other.scheduleEntry),
            s.scheduleEntry,
          ).isValid || !validateScheduleIsNotTooLong(s.scheduleEntry).isValid,
      }));
    },
    patchMissionsRestSet(state, action: PayloadAction<Partial<RestSet>>) {
      state.missions.rest = { ...state.missions.rest, ...action.payload };
    },
    patchSchedulesRestSet(state, action: PayloadAction<Partial<RestSet>>) {
      state.schedules.rest = { ...state.schedules.rest, ...action.payload };
    },
    patchCleaningZonesRestSet(state, action: PayloadAction<Partial<RestSet>>) {
      state.cleaningZones.rest = { ...state.cleaningZones.rest, ...action.payload };
    },
    patchTeachAndRepeatPathRestSet(state, action: PayloadAction<Partial<RestSet>>) {
      state.teachRepeatPaths.rest = { ...state.teachRepeatPaths.rest, ...action.payload };
    },
    patchRedZonesRestSet(state, action: PayloadAction<Partial<RestSet>>) {
      state.redZones.rest = { ...state.redZones.rest, ...action.payload };
    },
    patchLimitZonesRestSet(state, action: PayloadAction<Partial<RestSet>>) {
      state.limitZones.rest = { ...state.limitZones.rest, ...action.payload };
    },
    patchRuleZonesRestSet(state, action: PayloadAction<Partial<RestSet>>) {
      state.ruleZones.rest = { ...state.ruleZones.rest, ...action.payload };
    },
    patchGatesRestSet(state, action: PayloadAction<Partial<RestSet>>) {
      state.gates.rest = { ...state.gates.rest, ...action.payload };
    },
    patchLandmarksRestSet(state, action: PayloadAction<Partial<RestSet>>) {
      state.landmarks.rest = { ...state.landmarks.rest, ...action.payload };
    },
    patchWaypointsRestSet(state, action: PayloadAction<Partial<RestSet>>) {
      state.waypoints.rest = { ...state.waypoints.rest, ...action.payload };
    },
    patchRoomsRestSet(state, action: PayloadAction<Partial<RestSet>>) {
      state.rooms.rest = { ...state.rooms.rest, ...action.payload };
    },
    patchFloorsRestSet(state, action: PayloadAction<Partial<RestSet>>) {
      state.floors.rest = { ...state.floors.rest, ...action.payload };
    },
    dragLandmark(state, action: PayloadAction<Point>) {
      const landmark = state.landmarks.data.find((lm) => lm.id === state.landmarks.selectedId);
      if (landmark == null) {
        return;
      }
      landmark.landmarkPoint.coordinateX = action.payload.coordinateX;
      landmark.landmarkPoint.coordinateY = action.payload.coordinateY;
    },
    dragWayPoint(state, action: PayloadAction<Point>) {
      const node = state.waypoints.data.nodes.find((n) => n.id === state.waypoints.selectedNodeId);
      if (node == null) return;
      node.point = action.payload;
    },
    copyZoneById(state, action: PayloadAction<{ id: string; offset: number }>) {
      const selectedZoneInstance = selectZoneById(state, action.payload.id);
      const room = selectSelectedRoom(state);

      if (selectedZoneInstance == null || room == null) {
        return;
      }

      const isCleaningZoneCopy = isCleaningZone(selectedZoneInstance);
      const isTeachingZoneCopy = isTeachAndRepeatZone(selectedZoneInstance);
      const isRuleZoneCopy = isRuleZone(selectedZoneInstance);

      const newZoneName = getNewName(
        i18n.t(
          isTeachingZoneCopy
            ? 'teachandrepeat-zone'
            : isCleaningZoneCopy
              ? 'cleaning-zone'
              : isRuleZoneCopy
                ? 'rule-zone'
                : 'red-zone',
        ),
        isCleaningZoneCopy || isTeachingZoneCopy
          ? state.cleaningZones.data.filter((c) => c.name != null).map((c) => c.name!)
          : isRuleZoneCopy
            ? state.ruleZones.data.filter((c) => c.name != null).map((c) => c.name!)
            : state.redZones.data.filter((c) => c.name != null).map((c) => c.name!),
      );

      const allowedShift = getAllowedZoneShift(
        { coordinateX: action.payload.offset, coordinateY: -action.payload.offset }, // shifts it right and down if theres space
        selectedZoneInstance.points.map((p) => p.point),
        room,
      );

      const copiedPoints: { orderIndex: number; point: IdTypePoint }[] = [];
      selectedZoneInstance.points.forEach((p) => {
        copiedPoints.push({
          orderIndex: p.orderIndex,
          point: {
            id: p.point.id,
            type: p.point.type,
            coordinateX: p.point.coordinateX + allowedShift.coordinateX,
            coordinateY: p.point.coordinateY + allowedShift.coordinateY,
          },
        });
      });

      const newZone = {
        id: uuidv4(),
        name: newZoneName,
        room: selectedZoneInstance.room,
        points: copiedPoints,
      };

      if (isCleaningZoneCopy || isTeachingZoneCopy) {
        let startPosition: IdTypePoint | null = null;
        if (selectedZoneInstance.startPosition !== null) {
          startPosition = {
            id: selectedZoneInstance.startPosition.id,
            type: selectedZoneInstance.startPosition.type,
            coordinateX: selectedZoneInstance.startPosition.coordinateX + allowedShift.coordinateX,
            coordinateY: selectedZoneInstance.startPosition.coordinateY + allowedShift.coordinateY,
          };
        }
        state.cleaningZones.data.push({
          ...newZone,
          navigator: selectedZoneInstance.navigator,
          startPosition,
          fleetControlId: selectedZoneInstance.fleetControlId,
          cleaningConfiguration: { ...selectedZoneInstance.cleaningConfiguration },
          cleaningState: selectedZoneInstance.cleaningState,
          doWallFollow: selectedZoneInstance.doWallFollow,
        });

        if (isTeachingZoneCopy) {
          const existingPath = state.teachRepeatPaths.data.find(
            (p) => p.zoneId === selectedZoneInstance.id,
          );

          if (existingPath != null) {
            state.teachRepeatPaths.data.push({
              ...existingPath,
              id: uuidv4(),
              zoneId: newZone.id,
              path: existingPath.path.map((p) => ({
                ...p,
                id: uuidv4(),
                coordinateX: p.coordinateX + allowedShift.coordinateX,
                coordinateY: p.coordinateY + allowedShift.coordinateY,
              })),
            });
          }
        }
      } else if (isRuleZoneCopy) {
        state.ruleZones.data.push({
          ...newZone,
          mapRulesConfiguration: { ...selectedZoneInstance.mapRulesConfiguration },
        });
      } else {
        state.redZones.data.push({
          ...newZone,
          evacuation: selectedZoneInstance.evacuation,
          gateId: selectedZoneInstance.gateId,
        });
      }

      state.zonePlanner.selectedZone = {
        id: newZone.id,
        prevDragPoint: null,
        selectedPointId: null,
      };
    },
    handleWebsocketMessage(state, action: PayloadAction<WebSocketMessage>) {
      switch (action.payload.type) {
        case WebSocketMessageType.TEACHING:
          const message = action.payload.data;
          if (!isTeachingUpdateMessage(message)) return;
          state.zonePlanner.generatingTeachPathMessage = message;
          const selectedZone = state.cleaningZones.data.find((z) => z.id === message.zoneId);
          if (selectedZone != null) {
            if (
              message.type === TeachingUpdateType.AutoGeneratePathSuccess &&
              message.path != null
            ) {
              const selectedTrPath = state.teachRepeatPaths.data.find(
                (p) => p.zoneId === message.zoneId,
              );
              const pathPoints = message.path.map(
                (p) =>
                  ({
                    id: uuidv4(),
                    type: ZonePointType.TeachAndRepeatZonePathPoint,
                    coordinateX: p[0],
                    coordinateY: p[1],
                    coordinateTheta: p[2],
                    closeToObstacle: p[3],
                  }) as PathPoint,
              );
              if (selectedTrPath != null) {
                selectedTrPath.path = pathPoints;

                const lastAction = state.zonePlanner.history.undo.at(-1);
                if (
                  lastAction?.type === MapHistoryType.CleaningZone &&
                  lastAction.action === MapHistoryAction.Created &&
                  lastAction.additionalChangedData?.path.length === 2
                ) {
                  lastAction.additionalChangedData.path = pathPoints;
                }
              }
            }
          } else if (message.type === TeachingUpdateType.AutoGeneratePathError) {
            // storeApi.dispatch(deleteZoneById(message.zoneId));
          }
          break;
        default:
          break;
      }
    },
    resetGeneratingTeachPathMessage(state) {
      state.zonePlanner.generatingTeachPathMessage = null;
      state.zonePlanner.isGeneratingTeachPath = false;
    },
    setIsGeneratingTeachPath(state) {
      state.zonePlanner.isGeneratingTeachPath = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setSelectedRobotId, (state) => {
      // Reset state when id changes
      return {
        ...initialLocalRobotState,
      };
    });
    builder.addCase(getFloorsByRobotId.pending, (state, action) => {
      state.floors.rest.getStatus = HttpFetchStatus.Loading;
      state.floors.rest.getError = null;
    });
    builder.addCase(getFloorsByRobotId.fulfilled, (state, action) => {
      action.payload.forEach((floor) => {
        floor.rooms.forEach((roomApiData) => {
          updateStateWithRoomApiData(state, roomApiData);
        });
      });
      state.floors.data = mapFloorApiDataToFloorData(action.payload);
      state.floors.rest.getStatus = HttpFetchStatus.Success;
    });
    builder.addCase(getFloorsByRobotId.rejected, (state, action) => {
      state.floors.rest.getStatus = HttpFetchStatus.Failed;
      state.floors.rest.getError = action.payload ?? null;
    });
    builder.addCase(patchFloor.pending, (state, action) => {
      state.floors.rest.patchStatus = HttpFetchStatus.Loading;
      state.floors.rest.patchError = null;
    });
    builder.addCase(patchFloor.fulfilled, (state, action) => {
      const floorIndex = state.floors.data.findIndex((f) => f.id === action.meta.arg.id);
      if (floorIndex !== -1) {
        const floor = state.floors.data[floorIndex];
        state.floors.data[floorIndex] = { ...floor, ...action.payload };
      }
      state.floors.rest.patchStatus = HttpFetchStatus.Success;
    });
    builder.addCase(patchFloor.rejected, (state, action) => {
      state.floors.rest.patchStatus = HttpFetchStatus.Failed;
      state.floors.rest.patchError = action.payload ?? null;
    });
    builder.addCase(patchRoomById.pending, (state, action) => {
      state.rooms.rest.patchStatus = HttpFetchStatus.Loading;
      state.rooms.rest.patchError = null;
    });
    builder.addCase(patchRoomById.fulfilled, (state, action) => {
      const roomIndex = state.rooms.data.findIndex((f) => f.id === action.meta.arg.id);
      if (roomIndex !== -1) {
        const room = state.rooms.data[roomIndex];
        state.rooms.data[roomIndex] = { ...room, ...action.payload };
      }
      state.rooms.rest.patchStatus = HttpFetchStatus.Success;
    });
    builder.addCase(patchRoomById.rejected, (state, action) => {
      state.rooms.rest.patchStatus = HttpFetchStatus.Failed;
      state.rooms.rest.patchError = action.payload ?? null;
    });

    builder.addCase(deleteRobotMissionById.pending, (state, action) => {
      state.missions.rest.deleteStatus = HttpFetchStatus.Loading;
      state.missions.rest.deleteError = null;
    });
    builder.addCase(deleteRobotMissionById.fulfilled, (state, action) => {
      const missionIndex = state.missions.data.findIndex((m) => m.id === action.payload);
      if (missionIndex !== -1) {
        state.missions.data.splice(missionIndex, 1);
      }
      state.missions.rest.deleteStatus = HttpFetchStatus.Success;

      // delete schedules
      const missionSchedules = state.schedules.data.filter(
        (s) => s.scheduleEntry.missionId === action.payload,
      );
      if (missionSchedules.length > 0) {
        for (let ms of missionSchedules) {
          const scheduleEntryIndex = state.schedules.data.findIndex(
            (s) => s.scheduleEntry.id === ms.scheduleEntry.id,
          );
          if (scheduleEntryIndex !== -1) {
            state.schedules.data.splice(scheduleEntryIndex, 1);
          }
        }
      }
    });
    builder.addCase(deleteRobotMissionById.rejected, (state, action) => {
      state.missions.rest.deleteStatus = HttpFetchStatus.Failed;
      state.missions.rest.deleteError = action.payload ?? null;
    });
    builder.addCase(postNewRobotMission.pending, (state, action) => {
      state.missions.rest.postStatus = HttpFetchStatus.Loading;
      state.missions.rest.postError = null;
    });
    builder.addCase(postNewRobotMission.fulfilled, (state, action) => {
      state.missions.data = updateDataList(action.payload, state.missions.data);
      state.missions.rest.postStatus = HttpFetchStatus.Success;
    });
    builder.addCase(postNewRobotMission.rejected, (state, action) => {
      state.missions.rest.postStatus = HttpFetchStatus.Failed;
      state.missions.rest.postError = action.payload ?? null;
    });
    builder.addCase(patchRobotMission.pending, (state, action) => {
      state.missions.rest.patchStatus = HttpFetchStatus.Loading;
      state.missions.rest.patchError = null;
    });
    builder.addCase(patchRobotMission.fulfilled, (state, action) => {
      state.missions.data = updateDataList(action.payload, state.missions.data);
      state.missions.globalMissions = updateDataList(action.payload, state.missions.globalMissions);
      state.missions.rest.patchStatus = HttpFetchStatus.Success;
    });
    builder.addCase(patchRobotMission.rejected, (state, action) => {
      state.missions.rest.patchStatus = HttpFetchStatus.Failed;
      state.missions.rest.patchError = action.payload ?? null;
    });

    builder.addCase(getGlobalMissions.pending, (state, action) => {
      state.missions.rest.getStatus = HttpFetchStatus.Loading;
      state.missions.rest.getError = null;
    });
    builder.addCase(getGlobalMissions.fulfilled, (state, action) => {
      state.missions.globalMissions = action.payload;
      state.missions.rest.getStatus = HttpFetchStatus.Success;
    });
    builder.addCase(getGlobalMissions.rejected, (state, action) => {
      state.missions.rest.getStatus = HttpFetchStatus.Failed;
      state.missions.rest.getError = action.payload ?? null;
    });

    builder.addCase(deleteRobotScheduleById.pending, (state, action) => {
      state.schedules.rest.deleteStatus = HttpFetchStatus.Loading;
      state.schedules.rest.deleteError = null;
    });
    builder.addCase(deleteRobotScheduleById.fulfilled, (state, action) => {
      const scheduleEntryIndex = state.schedules.data.findIndex(
        (s) => s.scheduleEntry.id === action.payload,
      );
      if (scheduleEntryIndex !== -1) {
        state.schedules.data.splice(scheduleEntryIndex, 1);
      }
      state.schedules.rest.deleteStatus = HttpFetchStatus.Success;
    });
    builder.addCase(deleteRobotScheduleById.rejected, (state, action) => {
      state.schedules.rest.deleteStatus = HttpFetchStatus.Failed;
      state.schedules.rest.deleteError = action.payload ?? null;
    });
    builder.addCase(postNewRobotSchedule.pending, (state, action) => {
      state.schedules.rest.postStatus = HttpFetchStatus.Loading;
      state.schedules.rest.postError = null;
    });
    builder.addCase(postNewRobotSchedule.fulfilled, (state, action) => {
      const scheduleState = mapScheduleEntryToScheduleEntryState(action.payload);
      state.schedules.data = [...state.schedules.data, scheduleState];
      state.schedules.rest.postStatus = HttpFetchStatus.Success;
    });
    builder.addCase(postNewRobotSchedule.rejected, (state, action) => {
      state.schedules.rest.postStatus = HttpFetchStatus.Failed;
      state.schedules.rest.postError = action.payload ?? null;
    });

    builder.addCase(patchRobotSchedule.pending, (state, action) => {
      state.schedules.rest.patchStatus = HttpFetchStatus.Loading;
      state.schedules.rest.patchError = null;
    });
    builder.addCase(patchRobotSchedule.fulfilled, (state, action) => {
      const index = state.schedules.data.findIndex((s) => s.scheduleEntry.id === action.payload.id);
      if (index !== -1) {
        state.schedules.data[index] = mapScheduleEntryToScheduleEntryState(action.payload);
      }
      state.schedules.rest.patchStatus = HttpFetchStatus.Success;
    });
    builder.addCase(patchRobotSchedule.rejected, (state, action) => {
      state.schedules.rest.patchStatus = HttpFetchStatus.Failed;
      state.schedules.rest.patchError = action.payload ?? null;
    });

    builder.addCase(patchEnableAllSchedules.pending, (state, action) => {
      state.schedules.rest.patchStatus = HttpFetchStatus.Loading;
      state.schedules.rest.patchError = null;
    });
    builder.addCase(patchEnableAllSchedules.fulfilled, (state, action) => {
      state.schedules.data = state.schedules.data.map((entryState) => {
        if (
          isScheduleInFloor(
            entryState.scheduleEntry,
            action.payload.floor_id,
            state.missions.data,
            state.rooms.data,
          )
        ) {
          return {
            ...entryState,
            scheduleEntry: { ...entryState.scheduleEntry, enabled: action.payload.enabled },
          };
        }
        return entryState;
      });
      state.schedules.rest.patchStatus = HttpFetchStatus.Success;
    });
    builder.addCase(patchEnableAllSchedules.rejected, (state, action) => {
      state.schedules.rest.patchStatus = HttpFetchStatus.Failed;
      state.schedules.rest.patchError = action.payload ?? null;
    });

    builder.addCase(patchDisableAllSchedules.pending, (state, action) => {
      state.schedules.rest.patchStatus = HttpFetchStatus.Loading;
      state.schedules.rest.patchError = null;
    });
    builder.addCase(patchDisableAllSchedules.fulfilled, (state, action) => {
      state.schedules.data = state.schedules.data.map((entryState) => {
        if (
          isScheduleInFloor(
            entryState.scheduleEntry,
            action.payload.floor_id,
            state.missions.data,
            state.rooms.data,
          )
        ) {
          return {
            ...entryState,
            scheduleEntry: { ...entryState.scheduleEntry, enabled: action.payload.enabled },
          };
        }
        return entryState;
      });
      state.schedules.rest.patchStatus = HttpFetchStatus.Success;
    });
    builder.addCase(patchDisableAllSchedules.rejected, (state, action) => {
      state.schedules.rest.patchStatus = HttpFetchStatus.Failed;
      state.schedules.rest.patchError = action.payload ?? null;
    });

    builder.addCase(postNewCleaningZone.pending, (state, action) => {
      state.cleaningZones.rest.postStatus = HttpFetchStatus.Loading;
      state.cleaningZones.rest.postError = null;
    });
    builder.addCase(postNewCleaningZone.fulfilled, (state, action) => {
      state.cleaningZones.data = updateDataList(
        action.payload.cleaningZone,
        state.cleaningZones.data,
      );
      if (action.payload.teachAndRepeatPath != null) {
        state.teachRepeatPaths.data = updateDataList(
          action.payload.teachAndRepeatPath,
          state.teachRepeatPaths.data,
        );
      }
      state.cleaningZones.rest.postStatus = HttpFetchStatus.Success;
    });
    builder.addCase(postNewCleaningZone.rejected, (state, action) => {
      state.cleaningZones.rest.postStatus = HttpFetchStatus.Failed;
      state.cleaningZones.rest.postError = action.payload ?? null;
    });
    builder.addCase(patchCleaningZone.pending, (state, action) => {
      state.cleaningZones.rest.patchStatus = HttpFetchStatus.Loading;
      state.cleaningZones.rest.patchError = null;
    });
    builder.addCase(patchCleaningZone.fulfilled, (state, action) => {
      state.cleaningZones.data = updateDataList(
        action.payload.cleaningZone,
        state.cleaningZones.data,
      );
      if (action.payload.teachAndRepeatPath != null) {
        state.teachRepeatPaths.data = updateDataList(
          action.payload.teachAndRepeatPath,
          state.teachRepeatPaths.data,
        );
      }
      state.cleaningZones.rest.patchStatus = HttpFetchStatus.Success;
    });
    builder.addCase(patchCleaningZone.rejected, (state, action) => {
      if (state.zonePlanner.history.undo.length > 0) {
        const previousHistoryEntry =
          state.zonePlanner.history.undo[state.zonePlanner.history.undo.length - 1];
        state.zonePlanner.history.undo.pop();
        if (
          isHistoryCleaningZone(previousHistoryEntry.changedData) &&
          previousHistoryEntry.changedData.id === action.meta.arg.id
        ) {
          const previousPartialCleaningZoneState = previousHistoryEntry.changedData;
          const currentCleaningZoneState = state.cleaningZones.data.find(
            (z) => z.id === previousPartialCleaningZoneState.id,
          );
          const previousCleaningZoneState = {
            ...currentCleaningZoneState,
            ...previousPartialCleaningZoneState,
          };
          state.cleaningZones.data = [
            ...state.cleaningZones.data.filter((cz) => cz.id !== previousCleaningZoneState.id),
            previousCleaningZoneState,
          ];
          if (isTeachAndRepeatZone(previousCleaningZoneState)) {
            const correspondingTeachAndRepeatPath = state.teachRepeatPaths.data.find(
              (trp) => trp.zoneId === previousCleaningZoneState.id,
            );
            if (previousCleaningZoneState.startPosition && correspondingTeachAndRepeatPath) {
              correspondingTeachAndRepeatPath.path[0].coordinateX =
                previousCleaningZoneState.startPosition?.coordinateX;
              correspondingTeachAndRepeatPath.path[0].coordinateY =
                previousCleaningZoneState.startPosition?.coordinateY;
              state.teachRepeatPaths.data = [
                ...state.teachRepeatPaths.data.filter(
                  (trp) => trp.zoneId !== previousCleaningZoneState.id,
                ),
                correspondingTeachAndRepeatPath,
              ];
            }
          }
        } else if (
          isHistoryTeachAndRepeatZonePath(previousHistoryEntry.changedData) &&
          previousHistoryEntry.changedData.zoneId === action.meta.arg.id
        ) {
          const previousPartialTeachAndRepeatState = previousHistoryEntry.changedData;
          const currentTeachAndRepeatState = state.teachRepeatPaths.data.find(
            (z) => z.id === previousPartialTeachAndRepeatState?.id,
          );
          const previousTeachAndRepeatState = {
            ...currentTeachAndRepeatState,
            ...previousPartialTeachAndRepeatState,
          };
          const cleaningZoneState = state.cleaningZones.data.find(
            (cz) => cz.id === previousPartialTeachAndRepeatState.zoneId,
          );
          if (cleaningZoneState && cleaningZoneState.startPosition) {
            // this should never be false, but we need it for the type checking
            cleaningZoneState.startPosition.coordinateX =
              previousTeachAndRepeatState.path[0].coordinateX;
            cleaningZoneState.startPosition.coordinateY =
              previousTeachAndRepeatState.path[0].coordinateY;

            state.cleaningZones.data = [
              ...state.cleaningZones.data.filter(
                (cz) => cz.id !== previousPartialTeachAndRepeatState.zoneId,
              ),
              cleaningZoneState,
            ];
          }

          state.teachRepeatPaths.data = [
            ...state.teachRepeatPaths.data.filter(
              (trp) => trp.id !== previousPartialTeachAndRepeatState.id,
            ),
            previousTeachAndRepeatState,
          ];
        }
        state.cleaningZones.rest.patchStatus = HttpFetchStatus.Failed;
        state.cleaningZones.rest.patchError = action.payload ?? null;
      }
    });
    builder.addCase(deleteCleaningZoneById.pending, (state, action) => {
      state.cleaningZones.rest.deleteStatus = HttpFetchStatus.Loading;
      state.cleaningZones.rest.deleteError = null;
    });
    builder.addCase(deleteCleaningZoneById.fulfilled, (state, action) => {
      updateStateWithRoomApiData(state, action.payload);
      state.cleaningZones.rest.deleteStatus = HttpFetchStatus.Success;
    });
    builder.addCase(deleteCleaningZoneById.rejected, (state, action) => {
      state.cleaningZones.rest.deleteStatus = HttpFetchStatus.Failed;
      state.cleaningZones.rest.deleteError = action.payload ?? null;
    });
    // builder.addCase(patchTeachAndRepeatZonePath.fulfilled, (state, action) => {
    //   state.teachRepeatPaths.data = updateDataList(action.payload, state.teachRepeatPaths.data);
    //   state.teachRepeatPaths.rest.patchStatus = HttpFetchStatus.Success;
    // });

    // builder.addCase(patchTeachAndRepeatZonePath.pending, (state, action) => {
    //   state.teachRepeatPaths.rest.patchStatus = HttpFetchStatus.Loading;
    //   state.teachRepeatPaths.rest.patchError = null;
    // });
    // builder.addCase(patchTeachAndRepeatZonePath.rejected, (state, action) => {
    //   state.teachRepeatPaths.rest.patchStatus = HttpFetchStatus.Failed;
    //   state.teachRepeatPaths.rest.patchError = action.payload ?? null;
    // });

    builder.addCase(postNewRedZone.pending, (state, action) => {
      state.redZones.rest.postStatus = HttpFetchStatus.Loading;
      state.redZones.rest.postError = null;
    });
    builder.addCase(postNewRedZone.fulfilled, (state, action) => {
      state.redZones.data = updateDataList(action.payload, state.redZones.data);
      state.redZones.rest.postStatus = HttpFetchStatus.Success;
    });
    builder.addCase(postNewRedZone.rejected, (state, action) => {
      const previousRedZoneState = state.zonePlanner.history.undo.pop()?.changedData as RedZone;

      state.redZones.data = [
        ...state.redZones.data.filter((rz) => rz.id !== previousRedZoneState.id),
      ];

      state.redZones.rest.postStatus = HttpFetchStatus.Failed;
      state.redZones.rest.postError = action.payload ?? null;
    });
    builder.addCase(patchRedZone.pending, (state, action) => {
      state.redZones.rest.patchStatus = HttpFetchStatus.Loading;
      state.redZones.rest.patchError = null;
    });
    builder.addCase(patchRedZone.fulfilled, (state, action) => {
      state.redZones.data = updateDataList(action.payload, state.redZones.data);
      state.redZones.rest.patchStatus = HttpFetchStatus.Success;
    });
    builder.addCase(patchRedZone.rejected, (state, action) => {
      const previousPartialRedZoneState = state.zonePlanner.history.undo.pop()
        ?.changedData as Partial<RedZone>;
      const currentRedZoneState = state.redZones.data.find(
        (z) => z.id === previousPartialRedZoneState.id,
      );
      const previousRedZoneState = {
        ...currentRedZoneState,
        ...previousPartialRedZoneState,
      } as RedZone;

      state.redZones.data = [
        ...state.redZones.data.filter((rz) => rz.id !== previousRedZoneState.id),
        previousRedZoneState,
      ];

      state.redZones.rest.patchStatus = HttpFetchStatus.Failed;
      state.redZones.rest.patchError = action.payload ?? null;
    });
    builder.addCase(deleteRedZoneById.pending, (state, action) => {
      state.redZones.rest.deleteStatus = HttpFetchStatus.Loading;
      state.redZones.rest.deleteError = null;
    });
    builder.addCase(deleteRedZoneById.fulfilled, (state, action) => {
      const redZoneIndex = state.redZones.data.findIndex((rz) => rz.id === action.payload);
      if (redZoneIndex !== -1) {
        state.redZones.data.splice(redZoneIndex, 1);
      }
      state.redZones.rest.deleteStatus = HttpFetchStatus.Success;
    });
    builder.addCase(deleteRedZoneById.rejected, (state, action) => {
      state.redZones.rest.deleteStatus = HttpFetchStatus.Failed;
      state.redZones.rest.deleteError = action.payload ?? null;
    });
    builder.addCase(patchLimitZone.pending, (state, action) => {
      state.limitZones.rest.patchStatus = HttpFetchStatus.Loading;
      state.limitZones.rest.patchError = null;
    });
    builder.addCase(patchLimitZone.fulfilled, (state, action) => {
      state.limitZones.data = updateDataList(action.payload, state.limitZones.data);
      state.limitZones.rest.patchStatus = HttpFetchStatus.Success;
    });
    builder.addCase(patchLimitZone.rejected, (state, action) => {
      state.limitZones.rest.patchStatus = HttpFetchStatus.Failed;
      state.limitZones.rest.patchError = action.payload ?? null;
    });

    builder.addCase(postNewRuleZone.pending, (state, action) => {
      state.ruleZones.rest.postStatus = HttpFetchStatus.Loading;
      state.ruleZones.rest.postError = null;
    });
    builder.addCase(postNewRuleZone.fulfilled, (state, action) => {
      state.ruleZones.data = updateDataList(action.payload, state.ruleZones.data);
      state.ruleZones.rest.postStatus = HttpFetchStatus.Success;
    });
    builder.addCase(postNewRuleZone.rejected, (state, action) => {
      state.ruleZones.rest.postStatus = HttpFetchStatus.Failed;
      state.ruleZones.rest.postError = action.payload ?? null;

      state.ruleZones.data = [...state.ruleZones.data.filter((rz) => rz.id !== action.meta.arg.id)];
      state.zonePlanner.history.undo.pop();
    });
    builder.addCase(patchRuleZone.pending, (state, action) => {
      state.ruleZones.rest.patchStatus = HttpFetchStatus.Loading;
      state.ruleZones.rest.patchError = null;
    });
    builder.addCase(patchRuleZone.fulfilled, (state, action) => {
      state.ruleZones.data = updateDataList(action.payload, state.ruleZones.data);
      state.ruleZones.rest.patchStatus = HttpFetchStatus.Success;
    });
    builder.addCase(patchRuleZone.rejected, (state, action) => {
      const previousPartialRuleZoneState = state.zonePlanner.history.undo.pop()
        ?.changedData as Partial<RuleZone>;
      const currentRuleZoneState = state.ruleZones.data.find(
        (z) => z.id === previousPartialRuleZoneState.id,
      );
      const previousRuleZoneState = {
        ...currentRuleZoneState,
        ...previousPartialRuleZoneState,
      } as RuleZone;

      state.ruleZones.data = [
        ...state.ruleZones.data.filter((rz) => rz.id !== previousRuleZoneState.id),
        previousRuleZoneState,
      ];

      state.ruleZones.rest.patchStatus = HttpFetchStatus.Failed;
      state.ruleZones.rest.patchError = action.payload ?? null;
    });
    builder.addCase(deleteRuleZoneById.pending, (state, action) => {
      state.ruleZones.rest.deleteStatus = HttpFetchStatus.Loading;
      state.ruleZones.rest.deleteError = null;
    });
    builder.addCase(deleteRuleZoneById.fulfilled, (state, action) => {
      const ruleZoneIndex = state.ruleZones.data.findIndex((rz) => rz.id === action.payload);
      if (ruleZoneIndex !== -1) {
        state.ruleZones.data.splice(ruleZoneIndex, 1);
      }
      state.ruleZones.rest.deleteStatus = HttpFetchStatus.Success;
    });
    builder.addCase(deleteRuleZoneById.rejected, (state, action) => {
      state.ruleZones.rest.deleteStatus = HttpFetchStatus.Failed;
      state.ruleZones.rest.deleteError = action.payload ?? null;
    });

    builder.addCase(postNewLandmark.pending, (state, action) => {
      state.landmarks.rest.postStatus = HttpFetchStatus.Loading;
      state.landmarks.rest.postError = null;
    });
    builder.addCase(postNewLandmark.fulfilled, (state, action) => {
      state.landmarks.data = updateDataList(action.payload, state.landmarks.data);
      state.landmarks.rest.postStatus = HttpFetchStatus.Success;
    });
    builder.addCase(postNewLandmark.rejected, (state, action) => {
      state.landmarks.rest.postStatus = HttpFetchStatus.Failed;
      state.landmarks.rest.postError = action.payload ?? null;
    });
    builder.addCase(patchLandmark.pending, (state, action) => {
      state.landmarks.rest.patchStatus = HttpFetchStatus.Loading;
      state.landmarks.rest.patchError = null;
    });
    builder.addCase(patchLandmark.fulfilled, (state, action) => {
      state.landmarks.data = updateDataList(action.payload, state.landmarks.data);
      state.landmarks.rest.patchStatus = HttpFetchStatus.Success;
    });
    builder.addCase(patchLandmark.rejected, (state, action) => {
      state.landmarks.rest.patchStatus = HttpFetchStatus.Failed;
      state.landmarks.rest.patchError = action.payload ?? null;
    });
    builder.addCase(deleteLandmarkById.pending, (state, action) => {
      state.landmarks.rest.deleteStatus = HttpFetchStatus.Loading;
      state.landmarks.rest.deleteError = null;
    });
    builder.addCase(deleteLandmarkById.fulfilled, (state, action) => {
      updateStateWithRoomApiData(state, action.payload);
      state.landmarks.rest.deleteStatus = HttpFetchStatus.Success;
    });
    builder.addCase(deleteLandmarkById.rejected, (state, action) => {
      state.landmarks.rest.deleteStatus = HttpFetchStatus.Failed;
      state.landmarks.rest.deleteError = action.payload ?? null;
    });
    builder.addCase(upsertWaypointSet.pending, (state) => {
      state.waypoints.rest.postStatus = HttpFetchStatus.Loading;
      state.waypoints.rest.postError = null;
    });
    builder.addCase(upsertWaypointSet.fulfilled, (state, action) => {
      state.waypoints.rest.postStatus = HttpFetchStatus.Success;
      const roomId = action.meta.arg.room;

      state.waypoints.data.edges = [
        ...state.waypoints.data.edges.filter((e) => e.room !== roomId),
        ...action.payload.edges,
      ];
      state.waypoints.data.nodes = [
        ...state.waypoints.data.nodes.filter((n) => n.room !== roomId),
        ...action.payload.nodes,
      ];

      state.waypoints.unsavedWaypoints = state.waypoints.unsavedWaypoints.filter(
        (w) => w.room !== roomId,
      );
    });
    builder.addCase(upsertWaypointSet.rejected, (state, action) => {
      if (isUnsavedWaypointSet(action.payload)) {
        const pending = action.payload;

        state.waypoints.unsavedWaypoints = [
          ...state.waypoints.unsavedWaypoints.filter((w) => w.room !== pending.room),
          action.payload,
        ];

        state.waypoints.data.edges = [
          ...state.waypoints.data.edges.filter((e) => e.room !== pending.room),
          ...pending.waypointSet.edges,
        ];
        state.waypoints.data.nodes = [
          ...state.waypoints.data.nodes.filter((n) => n.room !== pending.room),
          ...pending.waypointSet.nodes,
        ];

        state.waypoints.rest.postStatus = HttpFetchStatus.Idle;
        state.waypoints.rest.postError = null;
      } else {
        state.waypoints.rest.postStatus = HttpFetchStatus.Failed;
        state.waypoints.rest.postError = action.payload ?? null;
      }
    });
    builder.addCase(postNewGate.pending, (state, action) => {
      state.gates.rest.postStatus = HttpFetchStatus.Loading;
      state.gates.rest.postError = null;
    });
    builder.addCase(postNewGate.fulfilled, (state, action) => {
      state.gates.rest.postStatus = HttpFetchStatus.Success;
      state.gates.selectedPointId = null;

      state.gates.data = updateDataList(action.payload.gate, state.gates.data);
      state.redZones.data = updateDataList(action.payload.redZone, state.redZones.data);
      updateStateWithRoomWaypointSetData(state, action.payload.waypointSet);
    });
    builder.addCase(postNewGate.rejected, (state, action) => {
      state.gates.rest.postStatus = HttpFetchStatus.Failed;
      state.gates.rest.postError = action.payload ?? null;
      state.gates.selectedId = null;
      state.gates.selectedPointId = null;

      const gateIndex = state.gates.data.findIndex((g) => g.id === state.gates.selectedId);
      if (gateIndex !== -1) {
        state.gates.data.splice(gateIndex, 1);
      }
    });
    builder.addCase(patchGateById.pending, (state, action) => {
      state.gates.rest.patchStatus = HttpFetchStatus.Loading;
      state.gates.rest.patchError = null;
    });
    builder.addCase(patchGateById.fulfilled, (state, action) => {
      state.gates.rest.patchStatus = HttpFetchStatus.Success;
      state.gates.selectedPointId = null;

      state.gates.data = updateDataList(action.payload.gate, state.gates.data);
      updateStateWithRoomWaypointSetData(state, action.payload.waypointSet);
    });
    builder.addCase(patchGateById.rejected, (state, action) => {
      state.gates.rest.patchStatus = HttpFetchStatus.Failed;
      state.gates.rest.patchError = action.payload ?? null;
      state.gates.selectedPointId = null;

      const historyEntry = state.zonePlanner.history.undo.pop()?.changedData;
      const gateIndex = state.gates.data.findIndex((g) => g.id === action.meta.arg.id);
      if (gateIndex !== -1 && historyEntry != null && isHistoryGateSet(historyEntry)) {
        state.gates.data[gateIndex] = historyEntry.gate;
        if (
          historyEntry.waypointSet?.nodes != null &&
          historyEntry.waypointSet.nodes.length === 1
        ) {
          state.waypoints.data.nodes = updateDataList(
            historyEntry.waypointSet.nodes[0],
            state.waypoints.data.nodes,
          );
        }
      }
    });
    builder.addCase(deleteGateById.pending, (state, action) => {
      state.gates.rest.deleteStatus = HttpFetchStatus.Loading;
      state.gates.rest.deleteError = null;
    });
    builder.addCase(deleteGateById.fulfilled, (state, action) => {
      state.gates.data = state.gates.data.filter((g) => g.id !== action.payload);
      state.gates.rest.deleteStatus = HttpFetchStatus.Success;

      // remove gateId from all redzones and waypoints
      state.redZones.data = state.redZones.data.map((rz) =>
        rz.gateId === action.payload ? { ...rz, gateId: '' } : rz,
      );
      state.waypoints.data.nodes = state.waypoints.data.nodes.map((wpn) =>
        wpn.gateId === action.payload ? { ...wpn, gateId: '' } : wpn,
      );
    });
    builder.addCase(deleteGateById.rejected, (state, action) => {
      state.gates.rest.deleteStatus = HttpFetchStatus.Failed;
      state.gates.rest.deleteError = action.payload ?? null;
    });
    builder.addCase(getErrors.pending, (state, action) => {
      state.errors.rest.getStatus = HttpFetchStatus.Loading;
      state.errors.rest.getError = null;
    });
    builder.addCase(getErrors.fulfilled, (state, action) => {
      if (action.payload.page === 1) {
        state.errors.data = action.payload ?? null;
      } else {
        state.errors.data = {
          ...action.payload, // update page/count/pageSize
          data: [...(state.errors.data?.data ?? []), ...action.payload.data],
        };
      }
      state.errors.rest.getStatus = HttpFetchStatus.Success;
    });
    builder.addCase(getErrors.rejected, (state, action) => {
      state.errors.rest.getStatus = HttpFetchStatus.Failed;
      state.errors.rest.getError = action.payload ?? null;
    });
  },
});

export const {
  resetLocalRobotState,
  setSelectedRoomId,
  toggleAllGatesHidden,
  toggleAllLandmarksHidden,
  toggleAllZonesHidden,
  toggleGateIdHidden,
  toggleLandmarkIdHidden,
  toggleZoneIdHidden,
  toggleWayPointVisibility,
  setZonePlannerMouseState,
  setZonePlannerMode,
  setSelectedLandmark,
  setSelectedZone,
  sendZoneToBack,
  patchStationDrag,
  updateSelectedZonePointCoordinates,
  addSelectedZonePoint,
  updateHistory,
  setSelectedGatePoint,
  setSelectedGate,
  patchSelectedGate,
  updateSelectedZoneCoordinatesOnDrag,
  setSelectedZoneSelectedPoint,
  setRoomLimitZoneSelectedPointId,
  setSelectedWayPoint,
  addRoomLimitZonePoint,
  addTeachAndRepeatPathPoint,
  setSelectedTeachAndRepeatZonePathPoint,
  updateRoomLimitZoneCoordinatesOnDrag,
  updateRoomLimitZonePointCoordinates,
  addLandmark,
  addWayPoint,
  updateWayPointMousePosition,
  setSelectedScheduleEntry,
  addNewDraftScheduleEntry,
  deleteDraftScheduleEntry,
  updateDraftScheduleEntry,
  patchMissionsRestSet,
  patchSchedulesRestSet,
  patchCleaningZonesRestSet,
  patchFloorsRestSet,
  patchGatesRestSet,
  patchLandmarksRestSet,
  patchLimitZonesRestSet,
  patchRedZonesRestSet,
  patchRuleZonesRestSet,
  patchWaypointsRestSet,
  patchRoomsRestSet,
  dragLandmark,
  dragWayPoint,
  copyZoneById,
  addUndoableAction,
  popUndoRedoAction,
  handleWebsocketMessage,
  resetGeneratingTeachPathMessage,
  setIsGeneratingTeachPath,
  updateSelectedTeachAndRepeatPathPointCoordinates,
} = localRobotSlice.actions;

export default localRobotSlice.reducer;
